import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import PitchBracketSVG from '../components/pitches/PitchBracketSVG';
import PitchSchedule from '../components/pitches/PitchSchedule';
import PitchSeeding from '../components/pitches/PitchSeeding';
import { ALL_PITCHES } from '../data/pitchData';
import '../styles/components/format-pitches.scss';

type PitchTab = 'bracket' | 'program' | 'seeding';

const VALID_TABS: PitchTab[] = ['bracket', 'program', 'seeding'];

const FormatPitchesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pitchParam = searchParams.get('pitch');
  const tabParam = searchParams.get('tab') as PitchTab | null;

  const [activePitch, setActivePitch] = useState<number>(
    pitchParam ? parseInt(pitchParam) : 1
  );
  const [activeTab, setActiveTab] = useState<PitchTab>(
    tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'bracket'
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Season 4 Format Pitches - Northern Championships Series';
  }, []);

  const handlePitchChange = (pitchId: number) => {
    setActivePitch(pitchId);
    const params = new URLSearchParams(searchParams);
    params.set('pitch', pitchId.toString());
    setSearchParams(params);
  };

  const handleTabChange = (tab: PitchTab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    params.set('tab', tab);
    setSearchParams(params);
  };

  // Sync state with URL changes (browser back/forward)
  useEffect(() => {
    const urlPitch = searchParams.get('pitch');
    const urlTab = searchParams.get('tab') as PitchTab | null;

    if (urlPitch) {
      const p = parseInt(urlPitch);
      if (p !== activePitch && ALL_PITCHES.some((pitch) => pitch.id === p)) {
        setActivePitch(p);
      }
    }

    if (urlTab && VALID_TABS.includes(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [searchParams, activePitch, activeTab]);

  const currentPitch = ALL_PITCHES.find((p) => p.id === activePitch) || ALL_PITCHES[0];

  return (
    <div className="format-pitches-page">
      <PageHeader
        title="SEASON 4 FORMAT"
        subtitle="Proposed tournament formats for the upcoming season"
      >
        <div className="tab-buttons">
          <button
            className={`tab-button ${activeTab === 'bracket' ? 'active' : ''}`}
            onClick={() => handleTabChange('bracket')}
          >
            Bracket
          </button>
          <button
            className={`tab-button ${activeTab === 'program' ? 'active' : ''}`}
            onClick={() => handleTabChange('program')}
          >
            Program
          </button>
          <button
            className={`tab-button ${activeTab === 'seeding' ? 'active' : ''}`}
            onClick={() => handleTabChange('seeding')}
          >
            Seeding
          </button>
        </div>
      </PageHeader>

      <div className="container">
        {/* Pitch selector - only shown when multiple pitches */}
        {ALL_PITCHES.length > 1 && (
          <div className="pitch-selector">
            {ALL_PITCHES.map((pitch) => (
              <button
                key={pitch.id}
                className={`pitch-button ${activePitch === pitch.id ? 'active' : ''}`}
                onClick={() => handlePitchChange(pitch.id)}
              >
                {pitch.name}
              </button>
            ))}
          </div>
        )}

        {/* Pitch description */}
        <p className="pitch-description">{currentPitch.description}</p>

        {/* Tab content */}
        <div className="pitch-content">
          {activeTab === 'bracket' && <PitchBracketSVG pitch={currentPitch} />}
          {activeTab === 'program' && <PitchSchedule pitch={currentPitch} />}
          {activeTab === 'seeding' && <PitchSeeding pitch={currentPitch} />}
        </div>
      </div>
    </div>
  );
};

export default FormatPitchesPage;
